import React, { useState, useRef, useEffect, ChangeEvent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppDispatch, RootState } from '../../store';
import { addPhoto, deletePhoto, updatePhoto, fetchPhotos } from '../../store/photosSlice';
import { Photo, Cover as CoverData, photosApi } from '../../api';
import { TopNav } from '../../components/TopNav/TopNav';
import {
  PhotoPage,
  PHOTO_PRESETS,
  PhotoPreset,
  PhotoTextSlot,
  PhotoTextSize,
  PhotoTextColor,
  encodePhotoLayout,
  decodePhotoLayout,
} from '../../components/PhotoPage';
import '../../styles/margin.css';
import './Photos.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SLOT_KEYS: Record<PhotoTextSlot, string> = {
  hidden: 'slot_hidden',
  top: 'slot_top',
  center: 'slot_center',
  bottom: 'slot_bottom',
};

const SIZE_KEYS: Record<PhotoTextSize, string> = {
  sm: 'size_sm',
  md: 'size_md',
  lg: 'size_lg',
};

const formatDateForDisplay = (d: Date | null): string => {
  if (!d || isNaN(d.getTime())) return '';
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
    .toString()
    .padStart(2, '0')}.${d.getFullYear()}`;
};

// yyyy-mm-dd по локальной таймзоне (чтобы input type="date" и save не
// разъезжались из-за UTC-смещения).
const toYmd = (d: Date | null): string => {
  if (!d || isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fromYmd = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  // local midnight, не UTC — getDate() потом даст ту же дату.
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const SAMPLE_PHOTO =
  '/covers/monet-water-lilies-setting-sun-1907.jpg';

export const Photos: React.FC = () => {
  const { templateId, photoId } = useParams<{ templateId: string; photoId?: string }>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation('photos');
  const { t: tCommon } = useTranslation('common');

  const searchParams = new URLSearchParams(location.search);
  const questionFromQuery = searchParams.get('question') || '';

  const coverData = useSelector<RootState, CoverData | null>(
    (state) => state.cover.value,
  );
  const photos = useSelector<RootState, Photo[]>((state) =>
    Object.values(state.photos.photos),
  );

  // ============== photo content ==============
  const [photoFile, setPhotoFile] = useState<string | null>(null);
  // Дата фото. null = юзер её не ставил — тогда подписи на странице нет
  // (галочка «показать дату» гаснет вместе с датой, см. showDateChk).
  const [photoDate, setPhotoDate] = useState<Date | null>(new Date());
  const [photoDescription, setPhotoDescription] = useState('');
  const [questionTxt, setQuestionTxt] = useState('');
  const [questions, setQuestions] = useState<{ id: number; number: number; question: string }[]>([]);

  // ============== photo layout (preset + slots + color) ==============
  const [preset, setPreset] = useState<PhotoPreset>('fullbleed');
  const [datePos, setDatePos] = useState<PhotoTextSlot>('top');
  const [dateSize, setDateSize] = useState<PhotoTextSize>('sm');
  const [showDateChk, setShowDateChk] = useState<boolean>(true);
  const [descPos, setDescPos] = useState<PhotoTextSlot>('bottom');
  const [descSize, setDescSize] = useState<PhotoTextSize>('md');
  const [showDescChk, setShowDescChk] = useState<boolean>(true);
  const [textColor, setTextColor] = useState<PhotoTextColor>('light');

  // ============== ui state ==============
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============== guards + fetches ==============

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { replace: true });
      return;
    }
    if (!templateId) navigate('/forms');
  }, [navigate, templateId]);

  // Тянем список фото, чтобы пагер мог отрисовать все страницы и активный пункт.
  useEffect(() => {
    if (!templateId) return;
    dispatch(fetchPhotos(Number(templateId)));
  }, [dispatch, templateId]);

  useEffect(() => {
    if (!templateId) return;
    const fetchQuestionsAndAnswers = async () => {
      try {
        const [questionsResponse, answersResponse] = await Promise.all([
          fetch(`${API_URL}/templates/${templateId}/questions`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }).then((res) => res.json()),
          fetch(`${API_URL}/answers/${templateId}`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }).then((res) => res.json()),
        ]);

        const filteredAnswers = (Array.isArray(answersResponse) ? answersResponse : []).filter(
          (answer: any) => answer.text !== '-' && answer.text.trim() !== '',
        );

        const questionsWithAnswers = (Array.isArray(questionsResponse) ? questionsResponse : [])
          .map((question: any, index: number) => {
            const answer = filteredAnswers.find((ans: any) => ans.questionId === question.id);
            return answer ? { id: question.id, number: index + 1, question: question.text } : null;
          })
          .filter((q: any): q is { id: number; number: number; question: string } => q !== null);

        setQuestions(questionsWithAnswers);
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };
    fetchQuestionsAndAnswers();
  }, [templateId]);

  useEffect(() => {
    if (questionFromQuery && questions.length > 0) {
      const found = questions.find((q) => q.question === questionFromQuery);
      if (found) setQuestionTxt(found.question);
    }
  }, [questions, questionFromQuery]);

  useEffect(() => {
    if (!templateId || !photoId) return;
    const fetchPhotoDetails = async () => {
      try {
        const { data: fetchedPhotos } = await photosApi.getByTemplate(Number(templateId));
        const photoDetails = fetchedPhotos.find((p) => String(p.id) === photoId);
        if (!photoDetails) return;

        // Дата отдельно от createdAt: photoDetails.date — yyyy-mm-dd | null.
        // Если null — оставляем null, чтобы превью не показывало
        // подставную дату (createdAt = upload-таймстемп, а не реальная
        // дата снимка).
        setPhotoDate(
          photoDetails.date ? fromYmd(photoDetails.date) : null,
        );
        setPhotoDescription(photoDetails.description || '');
        setPhotoFile(photoDetails.url);
        setQuestionTxt((photoDetails as any).questionTxt || '');

        const layout = decodePhotoLayout(photoDetails.position);
        setPreset(layout.preset);
        setDatePos(layout.date.pos === 'hidden' ? 'top' : layout.date.pos);
        setDateSize(layout.date.size);
        // Нет даты — нечего и показывать, галочка гаснет независимо от того,
        // что лежит в БД (там у старых фото остался showDate=true).
        setShowDateChk(
          Boolean(photoDetails.date) &&
            photoDetails.showDate !== false &&
            layout.date.pos !== 'hidden',
        );
        setDescPos(layout.desc.pos === 'hidden' ? 'bottom' : layout.desc.pos);
        setDescSize(layout.desc.size);
        setShowDescChk(photoDetails.showDescription !== false && layout.desc.pos !== 'hidden');
        setTextColor(layout.color);

        setHasChanges(false);
      } catch (error) {
        console.error('Failed to fetch photo details:', error);
      }
    };
    fetchPhotoDetails();
  }, [photoId, templateId]);

  // ============== handlers ==============

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleBack = () => {
    navigate('/forms');
  };

  const handlePhotoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowedFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic', 'image/webp'];
    if (!allowedFormats.includes(file.type)) {
      alert(t('alert_bad_format'));
      return;
    }
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setPhotoFile(response.data.url);
      setHasChanges(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(t('alert_upload_failed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!photoFile || !templateId || !questionTxt) {
      alert(t('alert_photo_question_required'));
      return;
    }
    setIsSaving(true);
    try {
      const positionEncoded = encodePhotoLayout({
        preset,
        date: { pos: effDatePos, size: dateSize },
        desc: { pos: effDescPos, size: descSize },
        color: textColor,
      });

      const dateStr = toYmd(photoDate) || null;

      if (photoId) {
        await dispatch(
          updatePhoto({
            id: Number(photoId),
            data: {
              description: photoDescription,
              url: photoFile,
              position: positionEncoded,
              showDate: showDateChk,
              showDescription: showDescChk,
              questionTxt,
              date: dateStr,
            },
          }),
        );
      } else {
        if (photos.length >= 100) {
          alert(t('alert_max_photos'));
          return;
        }
        await dispatch(
          addPhoto({
            templateId: Number(templateId),
            url: photoFile,
            description: photoDescription,
            position: positionEncoded,
            questionTxt,
            date: dateStr,
            // Без этого бэк ставил бы дефолтный showDate=true, и фото без
            // даты печаталось бы с датой загрузки файла.
            showDate: showDateChk,
            showDescription: showDescChk,
          }),
        );
      }
      setHasChanges(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2400);
    } catch (error) {
      console.error('Error saving photo:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!photoId) return;
    if (!window.confirm(t('confirm_delete') as string)) return;
    try {
      await dispatch(deletePhoto(Number(photoId)));
      navigate(`/forms`);
    } catch (error) {
      console.error('Failed to delete photo:', error);
    }
  };

  // ============== derived ==============

  const dateStr = useMemo(() => formatDateForDisplay(photoDate), [photoDate]);
  const noPhotoYet = !photoFile;

  // Стабильный порядок фото по id — чтобы номера в пагере не прыгали.
  const sortedPhotos = useMemo(
    () => [...photos].sort((a, b) => a.id - b.id),
    [photos],
  );
  const currentIndex = useMemo(
    () => sortedPhotos.findIndex((p) => String(p.id) === photoId),
    [sortedPhotos, photoId],
  );

  const goToPhotoIndex = (idx: number) => {
    if (idx < 0 || idx >= sortedPhotos.length) return;
    const target = sortedPhotos[idx];
    if (!target || String(target.id) === photoId) return;
    if (hasChanges && !window.confirm(t('unsaved_confirm') as string)) return;
    navigate(`/forms/photos/${templateId}/${target.id}`);
  };
  const handlePhotoPrev = () => goToPhotoIndex(currentIndex - 1);
  const handlePhotoNext = () => goToPhotoIndex(currentIndex + 1);

  // Эффективные слоты — для рендера превью. В стейте datePos/descPos
  // хранятся только top/center/bottom (без 'hidden'), а скрытость рулит
  // отдельный чекбокс.
  const effDatePos: PhotoTextSlot = showDateChk ? datePos : 'hidden';
  const effDescPos: PhotoTextSlot = showDescChk ? descPos : 'hidden';

  const cycleSlot = (current: PhotoTextSlot): PhotoTextSlot => {
    // Видимостью теперь рулит отдельный чекбокс под инпутом, поэтому
    // 'hidden' из цикла позиции убран — только top/center/bottom.
    const order: PhotoTextSlot[] = ['top', 'center', 'bottom'];
    const i = order.indexOf(current);
    return order[(i >= 0 ? i + 1 : 1) % order.length];
  };

  const cycleSize = (current: PhotoTextSize): PhotoTextSize => {
    const order: PhotoTextSize[] = ['sm', 'md', 'lg'];
    const i = order.indexOf(current);
    return order[(i + 1) % order.length];
  };

  // ============== render ==============

  return (
    <div className="mg-page mg-ph">
      <TopNav
        onLogout={handleLogout}
        back={handleBack}
        backLabel={t('crumb_home') as string}
        title={
          <>
            {(() => {
              const q = questions.find((x) => x.question === questionTxt);
              if (!q) return null;
              return (
                <>
                  <button
                    type="button"
                    className="mg-topnav__title-link"
                    onClick={() =>
                      navigate(`/forms/questions/${templateId}?q=${q.id}`)
                    }
                  >
                    {t('crumb_question', { n: q.number })}
                  </button>
                  <span className="mg-topnav__sep">/</span>
                </>
              );
            })()}
            <span>{t('crumb_photo')}</span>
          </>
        }
        actions={
          !noPhotoYet ? (
            <span className="mg-ph__topnav-actions">
              <span className={`mg-toast ${justSaved ? 'mg-toast--visible' : ''}`}>
                {t('toast_attached')}
              </span>
              <button
                className="mg-btn mg-ph__save"
                onClick={handleSave}
                disabled={isSaving || (!!photoId && !hasChanges)}
                type="button"
              >
                {isSaving
                  ? t('saving')
                  : photoId
                    ? hasChanges
                      ? t('save_edit')
                      : t('saved')
                    : t('attach_to_book')}
              </button>
            </span>
          ) : null
        }
      />

      <main className="mg-ph__main">

        {!noPhotoYet && (
          <div className="mg-ph__inpage-actions">
            <span className="mg-ph__inpage-title">
              {photoId ? t('rite_edit') : t('rite_new')}
            </span>
            <div className="mg-ph__inpage-actions-right">
              <span className={`mg-toast ${justSaved ? 'mg-toast--visible' : ''}`}>
                {t('toast_attached')}
              </span>
              <button
                className="mg-btn mg-ph__save"
                onClick={handleSave}
                disabled={isSaving || (!!photoId && !hasChanges)}
                type="button"
              >
                {isSaving
                  ? t('saving')
                  : photoId
                    ? hasChanges
                      ? t('save_edit')
                      : t('saved')
                    : t('attach_to_book')}
              </button>
            </div>
          </div>
        )}


        {noPhotoYet ? (
          <section className="mg-ph__upload">
            <div className="mg-chapter-mark mg-ph__upload-mark">
              <span className="mg-chapter-mark__roman">—</span>
              <span>{t('upload_mark')}</span>
            </div>
            <h1 className="mg-ph__upload-title">{t('upload_title')}</h1>
            <p className="mg-ph__upload-note">{t('upload_note')}</p>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
            />
            <button
              className="mg-btn mg-ph__upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              type="button"
            >
              {isUploading ? t('uploading') : t('choose_file')}
            </button>
            <div className="mg-meta mg-ph__upload-hint">{t('upload_hint')}</div>
          </section>
        ) : (
          <div className="mg-split">
            <section className="mg-ph__stage mg-split__preview">
              <PhotoPage
                className="mg-ph__preview"
                preset={preset}
                photoUrl={photoFile!}
                date={dateStr}
                datePos={effDatePos}
                dateSize={dateSize}
                description={photoDescription}
                descPos={effDescPos}
                descSize={descSize}
                textColor={textColor}
                footerTitle={coverData?.bookTitle || ''}
                pageNumber={95}
                safeZone
              />
            </section>

            <div className="mg-split__ui">

            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
            />
            <button
              className="mg-q__photo-attach mg-q__photo-attach--full"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              type="button"
            >
              <svg
                className="mg-q__photo-attach-icon"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <circle cx="8.5" cy="10" r="1.5" />
                <path d="M21 15l-5-5L5 19" />
              </svg>
              <span>{isUploading ? t('uploading') : t('choose_another')}</span>
            </button>

            {/* ============== preset picker ============== */}

            <section className="mg-ph__presets-section">
              <div className="mg-label mg-ph__presets-head">{t('presets_head')}</div>
              <div className="mg-ph__preset-grid">
                {PHOTO_PRESETS.map((p) => {
                  const isActive = p.id === preset;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`mg-ph__preset-btn ${
                        isActive ? 'mg-ph__preset-btn--active' : ''
                      }`}
                      onClick={() => {
                        setPreset(p.id);
                        setHasChanges(true);
                      }}
                    >
                      <PhotoPage
                        className="mg-ph__preset-thumb"
                        preset={p.id}
                        photoUrl={photoFile || SAMPLE_PHOTO}
                        date={dateStr || '15.07.1998'}
                        datePos={effDatePos}
                        dateSize={dateSize}
                        // Плейсхолдера нет: пустое описание должно быть пустым и
                        // на миниатюрах пресетов, а не «подпись к фото».
                        description={photoDescription}
                        descPos={effDescPos}
                        descSize={descSize}
                        textColor={textColor}
                        footerTitle={coverData?.bookTitle || ''}
                        pageNumber={95}
                      />
                      <div className="mg-ph__preset-text">
                        <div className="mg-ph__preset-name">{t(`preset_${p.id}_name`)}</div>
                        <div className="mg-ph__preset-desc">{t(`preset_${p.id}_desc`)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ============== caption fields ============== */}

            <section className="mg-ph__caption-section">
              <div className="mg-label" style={{ marginBottom: 28 }}>
                {t('caption_head')}
              </div>

              <div className="mg-ph__fields">
                <div className="mg-field">
                  <label className="mg-field__label">{t('field_date')}</label>
                  <input
                    type="date"
                    className="mg-input"
                    value={toYmd(photoDate)}
                    onChange={(e) => {
                      const next = fromYmd(e.target.value);
                      setPhotoDate(next);
                      // Дату стёрли — гасим галочку (показывать нечего);
                      // поставили после пустой — включаем обратно.
                      setShowDateChk(next !== null);
                      setHasChanges(true);
                    }}
                  />
                  <label className="mg-ph__show-toggle">
                    <input
                      type="checkbox"
                      checked={showDateChk}
                      disabled={photoDate === null}
                      onChange={(e) => {
                        setShowDateChk(e.target.checked);
                        setHasChanges(true);
                      }}
                    />
                    <span>{t('show_date')}</span>
                  </label>
                </div>

                <div className="mg-field">
                  <label className="mg-field__label">{t('field_description')}</label>
                  <textarea
                    className="mg-textarea mg-ph__desc"
                    placeholder={t('description_placeholder') as string}
                    value={photoDescription}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setPhotoDescription(e.target.value);
                        setHasChanges(true);
                      }
                    }}
                    rows={3}
                  />
                  <div className="mg-meta mg-ph__desc-count">
                    {photoDescription.length} / 200
                  </div>
                  <label className="mg-ph__show-toggle">
                    <input
                      type="checkbox"
                      checked={showDescChk}
                      onChange={(e) => {
                        setShowDescChk(e.target.checked);
                        setHasChanges(true);
                      }}
                    />
                    <span>{t('show_description')}</span>
                  </label>
                </div>

                <div className="mg-field mg-ph__select-field">
                  <label className="mg-field__label">{t('field_after_chapter')}</label>
                  <select
                    className="mg-input mg-ph__select"
                    value={questionTxt}
                    onChange={(e) => {
                      setQuestionTxt(e.target.value);
                      setHasChanges(true);
                    }}
                  >
                    <option value="">{t('choose_chapter')}</option>
                    {questions.map((q, idx) => (
                      <option key={idx} value={q.question}>
                        {q.number}. {q.question}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* ============== text controls ============== */}

            <section className="mg-ph__text-section">
              <div className="mg-label mg-ph__text-head">{t('text_head')}</div>

              <div className="mg-ph__text-group">
                <div className="mg-ph__text-group-name">{t('group_date')}</div>
                <div className="mg-ph__text-group-ctl">
                  <button
                    className="mg-ph__chip"
                    onClick={() => {
                      setDatePos(cycleSlot(datePos));
                      setHasChanges(true);
                    }}
                    type="button"
                  >
                    {t('chip_position')}<strong>{t(SLOT_KEYS[datePos])}</strong>
                  </button>
                  <button
                    className="mg-ph__chip"
                    disabled={!showDateChk}
                    onClick={() => {
                      setDateSize(cycleSize(dateSize));
                      setHasChanges(true);
                    }}
                    type="button"
                  >
                    {t('chip_size')}<strong>{t(SIZE_KEYS[dateSize])}</strong>
                  </button>
                </div>
              </div>

              <div className="mg-ph__text-group">
                <div className="mg-ph__text-group-name">{t('group_description')}</div>
                <div className="mg-ph__text-group-ctl">
                  <button
                    className="mg-ph__chip"
                    onClick={() => {
                      setDescPos(cycleSlot(descPos));
                      setHasChanges(true);
                    }}
                    type="button"
                  >
                    {t('chip_position')}<strong>{t(SLOT_KEYS[descPos])}</strong>
                  </button>
                  <button
                    className="mg-ph__chip"
                    disabled={!showDescChk}
                    onClick={() => {
                      setDescSize(cycleSize(descSize));
                      setHasChanges(true);
                    }}
                    type="button"
                  >
                    {t('chip_size')}<strong>{t(SIZE_KEYS[descSize])}</strong>
                  </button>
                </div>
              </div>

              <div className="mg-ph__color-row">
                <div className="mg-ph__color-head">{t('color_head')}</div>
                <div className="mg-ph__color-btns">
                  <button
                    type="button"
                    className={`mg-ph__color-btn mg-ph__color-btn--light ${
                      textColor === 'light' ? 'mg-ph__color-btn--active' : ''
                    }`}
                    onClick={() => {
                      setTextColor('light');
                      setHasChanges(true);
                    }}
                  >
                    <span className="mg-ph__color-swatch" />
                    {t('color_light')}
                  </button>
                  <button
                    type="button"
                    className={`mg-ph__color-btn mg-ph__color-btn--dark ${
                      textColor === 'dark' ? 'mg-ph__color-btn--active' : ''
                    }`}
                    onClick={() => {
                      setTextColor('dark');
                      setHasChanges(true);
                    }}
                  >
                    <span className="mg-ph__color-swatch" />
                    {t('color_dark')}
                  </button>
                </div>
              </div>
            </section>

            {sortedPhotos.length > 1 && currentIndex >= 0 && (
              <nav className="mg-q__pager" aria-label={t('pager_label') as string}>
                <button
                  type="button"
                  className="mg-q__pager-arrow"
                  onClick={handlePhotoPrev}
                  disabled={currentIndex === 0}
                  aria-label={t('prev_page') as string}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M15 6l-6 6 6 6" />
                  </svg>
                </button>

                {(() => {
                  const total = sortedPhotos.length;
                  const cur = currentIndex + 1;
                  const w = 2;
                  const lo = Math.max(1, cur - w);
                  const hi = Math.min(total, cur + w);
                  const items: (number | 'gap')[] = [];
                  if (lo > 1) {
                    items.push(1);
                    if (lo > 2) items.push('gap');
                  }
                  for (let i = lo; i <= hi; i += 1) items.push(i);
                  if (hi < total) {
                    if (hi < total - 1) items.push('gap');
                    items.push(total);
                  }
                  return items.map((it, i) =>
                    it === 'gap' ? (
                      <span key={`gap-${i}`} className="mg-q__pager-gap" aria-hidden>…</span>
                    ) : (
                      <button
                        key={sortedPhotos[it - 1].id}
                        type="button"
                        className={`mg-q__pager-num ${it === cur ? 'mg-q__pager-num--active' : ''}`}
                        onClick={() => goToPhotoIndex(it - 1)}
                        aria-current={it === cur ? 'page' : undefined}
                      >
                        {it}
                      </button>
                    ),
                  );
                })()}

                <button
                  type="button"
                  className="mg-q__pager-arrow"
                  onClick={handlePhotoNext}
                  disabled={currentIndex === sortedPhotos.length - 1}
                  aria-label={t('next_page') as string}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              </nav>
            )}

            {photoId && (
              <section className="mg-ph__danger">
                <button
                  className="mg-ph__delete"
                  onClick={handleDelete}
                  type="button"
                >
                  <svg
                    className="mg-ph__delete-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                  <span>{t('delete_photo')}</span>
                </button>
              </section>
            )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
